// frontend/src/tools.js
// Single source of truth for the widget catalogue: landing page grid,
// /tools/<slug> pages, nav menus, and footer links all read from here.
// Demo components are lazy so a tool's JS only loads on its own page.
import { lazy } from 'react';
import { WIDGET_REFERENCE, groupByCategory } from './config/widgetReference';
import {
  Calculator,
  Cat,
  Landmark,
  CalendarClock,
  MapPin,
  Smartphone,
  Volume2,
  Home,
  Thermometer,
} from 'lucide-react';

const TOOLS = [
  {
    slug: 'savings-calculator',
    name: 'Savings calculator',
    icon: Calculator,
    card: 'Sizes heat pumps, solar, and batteries for a specific UK property, then estimates costs, savings, and carbon reduction.',
    title: 'Savings calculator',
    lede: 'Enter a postcode or property details below. The calculator sizes heat pumps, solar panels, and batteries for the specific property, then estimates costs, savings, and carbon reduction.',
    embedPitch: 'Choose the colours and which technologies it covers, then paste one line of code into any page.',
    demo: lazy(() => import('./components/LCTCalculator/LCTCalculator')),
    demoCard: 'surface',
    seo: {
      title: 'Savings calculator widget – heat pump, solar & battery sizing',
      description: 'Embeddable calculator that sizes heat pumps, solar PV, and batteries for a specific UK property using EPC data, then estimates costs, savings, and carbon reduction.',
    },
  },
  {
    slug: 'grant-finder',
    name: 'Grant finder',
    icon: Landmark,
    card: 'Enter a postcode and answer four short questions to see which UK grants and funding schemes a household could get.',
    title: 'Grant finder',
    lede: 'Your users enter a postcode and answer four short questions to see what funding they could get: the £7,500 heat pump grant, free insulation schemes, council upgrades worth up to £30,000. Each result links to the official government page, with the date we last checked the scheme.',
    embedPitch: 'Answers “what help can I get?” on your own site, with scheme data checked against official sources on a rolling cycle.',
    demo: lazy(() => import('./components/GrantFinder/GrantFinderWidget')),
    demoCard: 'surface',
    seo: {
      title: 'Grant finder widget – UK heat pump & insulation funding',
      description: 'Embeddable widget: postcode plus four questions shows which UK funding schemes a household may qualify for – BUS, ECO4, Warm Homes, Home Energy Scotland, Nest, and more.',
    },
  },
  {
    slug: 'boiler-escape-plan',
    name: 'Boiler escape plan',
    icon: CalendarClock,
    card: 'Helps homeowners plan a boiler replacement before it breaks down, with a personalised readiness plan and calendar reminders. Covers gas, oil and LPG.',
    title: 'Boiler escape plan',
    lede: 'Most UK homes will replace a boiler in a panic when it dies. This widget shows the carbon arithmetic of acting now versus locking in another decade of fossil heat, and turns into a personalised readiness plan with calendar reminders. It covers mains gas, heating oil and LPG – off-gas homes get their own numbers, and the strongest case for switching.',
    embedPitch: 'Reaches homeowners two to five years before their boiler fails, with a plan carrying your name.',
    demo: lazy(() => import('./components/BoilerEscapePlan/BoilerEscapePlan')),
    demoCard: 'white-padded',
    seo: {
      title: 'Boiler escape plan widget – plan the switch before the boiler dies',
      description: 'Embeddable widget that shows the carbon cost of waiting for a gas, oil or LPG boiler to fail and builds a personalised heat pump readiness plan with calendar reminders.',
    },
  },
  {
    slug: 'radiator-check',
    name: 'Radiator check',
    icon: Thermometer,
    card: 'Which radiators would need changing for a heat pump, and which are a straight swap – from the radiator type in each room, no measuring.',
    title: 'Radiator check',
    lede: 'Heat pumps run radiators cooler than boilers do, so some radiators may need to be bigger to keep a room as warm, and others already have capacity to spare. Four short steps, with no tape measure: the boiler’s heating control, the rooms, which get warm enough, and the type of each radiator. Then, room by room, which are likely fine at 40–55°C, which are a straight swap for a deeper radiator in the same space, and the flow temperature the home could run at now.',
    bullets: [
      'Four short steps, no measurements',
      'Likely fine, straight swap or more wall space, room by room',
      'A free boiler test to firm up a cautious answer',
    ],
    embedPitch: 'Answers “will I have to change all my radiators?” before a survey. On a paid plan, the result can come to you as an enquiry.',
    demo: lazy(() => import('./components/RadiatorCheck/RadiatorCheckWidget')),
    demoCard: 'surface',
    seo: {
      title: 'Radiator check widget – are your radiators ready for a heat pump?',
      description: 'Embeddable widget: pick the radiator type in each room and see which would need changing for a heat pump at 40–55°C, which are straight swaps, and the flow temperature the home could run at.',
    },
  },
  {
    slug: 'installer-finder',
    name: 'Installer finder',
    icon: MapPin,
    card: 'Postcode search for nearby renewable energy installers, filtered by technology, with contact details and MCS verification links.',
    title: 'Find a renewable energy installer',
    lede: 'Your users enter a postcode to find nearby renewable energy installers. Results show distance, contact details, which technologies each installer offers (and where we found that out), and a link to check them on the MCS register.',
    embedPitch: 'Help your visitors find a local installer. An installer can show only their own business by choosing “your own list” as the source in the dashboard.',
    demo: lazy(() => import('./components/InstallerFinder/InstallerFinderWidget')),
    demoCard: 'surface',
    seo: {
      title: 'Installer finder widget – local renewable energy installers',
      description: 'Embeddable postcode search for nearby UK renewable energy installers, filtered by heat pump, solar PV, or battery, with MCS verification links.',
    },
  },
  {
    slug: 'heat-pump-visualiser',
    name: 'Heat pump visualiser',
    icon: Smartphone,
    card: 'Preview a life-size heat pump in the garden using augmented reality on a phone or tablet. No app download.',
    title: 'See it in your space before you buy',
    lede: 'Your customers can preview a heat pump in their garden using augmented reality on a phone or tablet. No app download needed.',
    bullets: [
      'Pinch to scale, drag to reposition',
      'Works on iOS Safari and Android Chrome',
      'Floor-anchored placement for realistic preview',
    ],
    embedPitch: 'Choose the model and colours, then paste one line of code into your page.',
    demo: lazy(() => import('./EnhancedARWidget')),
    demoProps: {
      modelId: '7',
      configId: 'demo-basic-heat-pump',
      requireAuth: false,
      arEnhancements: {
        enableAdvancedInteractions: true,
        allowScale: true,
        allowRotation: true,
        arScale: 'auto',
        arPlacement: 'floor',
        minScale: 0.1,
        maxScale: 5.0,
      },
    },
    demoCard: 'white-padded',
    seo: {
      title: 'Heat pump visualiser widget – preview it in your space with AR',
      description: 'Embeddable AR widget that lets users place a life-size heat pump in their garden from any mobile browser. WebXR and AR Quick Look, no app download.',
    },
  },
  {
    slug: 'sound-simulator',
    name: 'Sound simulator',
    icon: Volume2,
    card: 'Play a real heat pump recording and measure the decibel level from anywhere around the home using the phone microphone.',
    title: 'Heat pump sound simulator',
    lede: 'Play a real heat pump recording through a speaker placed where the unit would go, then use the built-in decibel meter to check how it sounds from anywhere around your home.',
    embedPitch: 'Lets visitors hear a real heat pump on your own site.',
    demo: lazy(() => import('./components/HeatPumpSoundExperience')),
    demoCard: 'none',
    seo: {
      title: 'Heat pump sound simulator widget – hear it before you buy',
      description: 'Embeddable widget that plays a real heat pump recording and measures decibel levels at different distances using the phone microphone.',
    },
  },
  {
    slug: 'house-tour',
    name: '3D house tour',
    icon: Home,
    card: 'A 3D cutaway of an all-electric home: heat pump, hot water, radiators, solar, a battery, car charging and cooking, with a guided tour of how it fits together.',
    title: 'Interactive 3D house tour',
    lede: 'Look inside an ordinary 1930s semi that runs entirely on electricity. A guided tour flies through the heat pump, hot water cylinder, radiators, solar panels, home battery, car charger and induction hob, and twenty things in all are explained in plain English, each with its sources. Switch from afternoon to evening to see the battery and the cheap overnight hours at work.',
    bullets: [
      'A guided tour, then twenty things to explore room by room',
      'Afternoon and evening show solar, battery and cheap hours at work',
      'A plain-text list of everything for any device that cannot draw it',
    ],
    embedPitch: 'Shows how a whole all-electric home fits together, without a site visit.',
    demo: lazy(() => import('./components/HouseTour/TourFrame')),
    demoProps: { guide: 'none', title: '3D house tour: an all-electric home', primaryColor: '#2d6a4f' },
    demoCard: 'white',
    seo: {
      title: '3D house tour widget – see a whole all-electric home',
      description: 'Embeddable 3D cutaway of an all-electric home with a guided tour: heat pump, hot water cylinder, radiators, solar panels, battery, car charging and induction cooking.',
    },
  },
  {
    slug: 'watts-house',
    name: "Watt's House",
    icon: Cat,
    card: 'The same all-electric house, explored with Watt the cat. Send him round the house and whatever he sits beside is explained.',
    title: "Watt's House",
    lede: 'An ordinary 1930s semi that runs entirely on electricity, and Watt, the resident cat. Tap where he should go and whatever he sits beside is explained in plain English, with its sources – or follow him on a ten-stop tour. It covers the same twenty things as the 3D house tour, for audiences who would rather be shown round than read a brochure.',
    bullets: [
      'Watt takes visitors round a ten-stop tour',
      'Tap anywhere and he walks there, upstairs too',
      'The same twenty topics and sources as the 3D house tour',
    ],
    embedPitch: 'A friendlier way into electrification for a general audience, families and schools.',
    demo: lazy(() => import('./components/HouseTour/TourFrame')),
    demoProps: { guide: 'watt', title: "Watt's House: an all-electric home, guided by Watt the cat", primaryColor: '#2d6a4f' },
    demoCard: 'white',
    seo: {
      title: "Watt's House widget – an all-electric home, guided by a cat",
      description: 'Embeddable 3D walk-around of an all-electric home guided by Watt the cat: heat pump, solar, battery, car charging and cooking, each explained in plain English.',
    },
  },
];

// Each tool's category comes from its WIDGET_REFERENCE row (by slug), so the
// groups on the site are the groups in /docs, llms.txt and widgets.json.
TOOLS.forEach((tool) => {
  const row = WIDGET_REFERENCE.find((w) => w.slug === tool.slug);
  tool.category = row ? row.category : undefined;
});

export const TOOL_GROUPS = groupByCategory(TOOLS, (tool) => tool.category);

export const getTool = (slug) => TOOLS.find((t) => t.slug === slug);

export default TOOLS;
